import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useFocusEffect} from '@react-navigation/native';
import globalStore from '@/services/global.state';
import {goTo, toAgentApply, toPriceStr} from '@/utils';
import {useLogin, useUserInfo} from '@/services/global.store';
import {
  getUserList,
  getUserTotal,
} from '@/common-pages/invitation/invitation.service';
import {UserListItem} from '@/common-pages/invitation/invitation.type';
import {useShare} from '@/common-pages/hooks/share.hooks';

const referralBg = require('@/assets/imgs/referral/bg.webp');
const invitationBg = require('@/assets/imgs/referral/invit-bg.webp');
const incomeIcon = require('@/assets/imgs/referral/income.webp');
const countIcon = require('@/assets/imgs/referral/count.webp');

const money = (value?: number) => toPriceStr(value || 0, {fixed: 0});

const RefreshIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" className="h-3.5 w-3.5">
    <path
      d="M11.4 4.2A4.7 4.7 0 0 0 2.8 3.1M2.6 1.2v2.6h2.6M2.6 9.8a4.7 4.7 0 0 0 8.6 1.1m.2 1.9v-2.6H8.8"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RuleIcon = ({light}: {light?: boolean}) => (
  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
    <path
      d="M6 3h10l2 2v16H6V3Z"
      fill={light ? '#FFFFFF' : '#848491'}
      opacity="0.95"
    />
    <path
      d="M8 8h6M8 12h8M8 16h5"
      stroke={light ? '#8B5A23' : '#FFFFFF'}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M16 3v4h4"
      stroke={light ? '#FFFFFF' : '#848491'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RecordIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
    <path d="M4 5h16v4H4V5Zm0 6h16v4H4v-4Zm0 6h10v2H4v-2Z" fill="#848491" />
    <circle cx="17.5" cy="17.5" r="3" stroke="#848491" strokeWidth="1.5" />
    <path
      d="M17.5 16v1.8l1.4.9"
      stroke="#848491"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowRight = () => (
  <svg viewBox="0 0 14 14" fill="none" className="h-3.5 w-3.5">
    <path
      d="M5.25 10.5L8.75 7L5.25 3.5"
      stroke="#9FA5AC"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Header = ({isTop, onRules}: {isTop: boolean; onRules: () => void}) => (
  <div
    className={`fixed top-0 z-10 flex h-11 w-full max-w-[var(--max-screen-width)] items-center justify-center ${
      isTop
        ? 'bg-transparent text-white shadow-none'
        : 'bg-white text-[var(--font-color-main)] shadow-[0_1px_4px_rgba(0,0,0,0.25)]'
    }`}>
    <div className="text-base font-bold">Invite Friends</div>
    <button
      type="button"
      onClick={onRules}
      className="absolute right-3 top-0 flex h-full items-center border-0 bg-transparent p-0">
      <RuleIcon light={isTop} />
      <span
        className={`ml-2 text-xs font-normal ${
          isTop ? 'text-white' : 'text-[var(--font-color-second)]'
        }`}>
        Rules
      </span>
    </button>
  </div>
);

const InvitationAction = ({
  title,
  type,
  onClick,
}: {
  title: string;
  type: 'record' | 'rules';
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-7 w-[8.94rem] items-center justify-between border-0 bg-transparent p-0">
    <div className="flex items-center">
      {type === 'record' ? <RecordIcon /> : <RuleIcon />}
      <div className="ml-2 w-20 text-left text-xs font-normal leading-none text-[var(--font-color-second)]">
        {title}
      </div>
    </div>
    <ArrowRight />
  </button>
);

const InviteCodeText = ({code}: {code: string}) => {
  const chars = (code || '------').split('');

  return (
    <div className="flex h-[3.25rem] w-[12.0625rem] items-center bg-white px-4 text-[1.125rem] font-bold leading-none tracking-[-0.01875rem] text-[var(--font-color-main)]">
      <div className="flex h-[3.25rem] w-full flex-row items-center justify-between">
        {chars.map((item, index) => (
          <div
            key={`${item}-${index}`}
            className="flex h-[3.25rem] flex-1 items-center justify-center">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

const BonusCard = ({
  item,
  onShare,
}: {
  item: UserListItem;
  onShare: () => void;
}) => {
  const completed = item.inviteNumber === item.completedNumber;

  return (
    <div className="relative h-60 overflow-hidden rounded-lg bg-white">
      <div className="absolute left-0 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#EFF2F7]" />
      <div className="absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 translate-x-1/2 rounded-full bg-[#EFF2F7]" />
      <div className="flex h-10 items-center justify-between border-b border-[#E8EAEE] px-3 py-3 text-base font-bold">
        <div className="text-[var(--font-color-main)]">{`Bouns ${item.level}`}</div>
        <div className="text-[var(--basic-color-primary)]">
          {money(item.bonusAmount)}
        </div>
      </div>
      <div className="px-3 py-3">
        <div className="flex h-[3.75rem] flex-col justify-between rounded-[0.13375rem] text-xs">
          <div className="flex h-7 items-center justify-between bg-[#F0F2F6] px-3">
            <span className="font-normal text-[var(--font-color-second)]">
              Number of invitees
            </span>
            <span className="font-bold text-[var(--font-color-main)]">
              {item.inviteNumber}
            </span>
          </div>
          <div className="flex h-7 items-center justify-between bg-[#F0F2F6] px-3">
            <span className="font-normal text-[var(--font-color-second)]">
              Recharge per people
            </span>
            <span className="font-bold text-[var(--basic-color-primary)]">
              {money(item.depositAmount)}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-3">
          <div className="flex h-[2.625rem] w-[7.5rem] flex-col items-center justify-center">
            <div className="text-base font-bold text-[var(--font-color-second)]">
              <span className="text-[var(--basic-color-primary)]">
                {item.invitedNumber}
              </span>
              {` / ${item.inviteNumber}`}
            </div>
            <div className="mt-1 text-xs font-normal text-[#666]">
              Number of invitees
            </div>
          </div>
          <div className="flex h-[2.625rem] w-[7.5rem] flex-col items-center justify-center">
            <div className="text-base font-bold text-[var(--font-color-second)]">
              <span className="text-[var(--basic-color-primary)]">
                {item.completedNumber}
              </span>
              {` / ${item.inviteNumber}`}
            </div>
            <div className="mt-1 text-xs font-normal text-[#666]">
              Deposit number
            </div>
          </div>
        </div>
        <button
          type="button"
          disabled={completed}
          onClick={onShare}
          className="h-10 w-full rounded-full border-0 bg-[var(--basic-color-primary)] text-sm font-bold text-white disabled:opacity-60">
          {item.inviteNumber === item.invitedNumber
            ? 'Completed'
            : 'Go Complete'}
        </button>
      </div>
    </div>
  );
};

const Referral = () => {
  const login = useLogin();
  const userInfo = useUserInfo();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isTop, setIsTop] = useState(true);
  const [allAmount, setAllAmount] = useState(0);
  const [invitedNumber, setInvitedNumber] = useState(0);
  const [userList, setUserList] = useState<UserListItem[]>([]);
  const {code, doShare, initShare, refreshCode} = useShare();
  const canLoad =
    login || !!globalStore.token || !!localStorage.getItem('token');

  const loadData = useCallback(() => {
    if (!canLoad) {
      return;
    }

    Promise.allSettled([
      initShare(),
      getUserTotal().then(data => {
        setAllAmount(data.bonusAmountTotal);
        setInvitedNumber(data.invitedNumberTotal);
      }),
      getUserList().then(setUserList),
    ]);
  }, [canLoad, initShare]);

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData]),
  );

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) {
      return;
    }

    const handleScroll = () => {
      setIsTop(body.scrollTop < 20);
    };

    body.addEventListener('scroll', handleScroll);
    return () => body.removeEventListener('scroll', handleScroll);
  }, []);

  const handleResetLink = useCallback(async () => {
    if (!login) {
      goTo('Login');
      return;
    }
    globalStore.globalLoading.next(true);
    try {
      await refreshCode();
    } finally {
      globalStore.globalLoading.next(false);
    }
  }, [login, refreshCode]);

  const handleShare = useCallback(() => {
    doShare();
  }, [doShare]);

  return (
    <div className="relative h-full overflow-hidden bg-[#EFF2F7]">
      <Header isTop={isTop} onRules={() => goTo('InvitationRule')} />
      <div
        ref={bodyRef}
        className="relative h-full overflow-y-auto bg-[#EFF2F7]">
        <img
          src={referralBg}
          alt=""
          className="pointer-events-none absolute left-0 top-[-2.75rem] h-[23.75rem] w-full object-cover"
        />
        <div className="relative px-3 pb-[4.5rem] pt-[10.25rem]">
          <div
            className="h-[6.25rem] w-full bg-cover bg-no-repeat pl-3 "
            style={{backgroundImage: `url(${invitationBg})`}}>
            <div className="flex h-9 items-center justify-between">
              <div className="text-sm font-bold text-[#6E350F]">
                My invitation code
              </div>
              <button
                type="button"
                onClick={handleResetLink}
                className="mr-0 flex h-7 items-center rounded bg-white px-3 text-xs font-medium text-[var(--basic-color-primary)]">
                <RefreshIcon />
                <span className="ml-1">Reset Link</span>
              </button>
            </div>
            <div className="flex h-16 items-center justify-between pr-8">
              <InviteCodeText code={code} />
              <button
                type="button"
                onClick={handleShare}
                className="h-8 w-[5.25rem] rounded-[1.25rem] border-0 bg-[var(--basic-color-primary)] text-sm font-bold text-white">
                Copy
              </button>
            </div>
          </div>

          <div className="mt-3 bg-white px-6 py-3">
            <div className="mb-4 flex flex-col items-center justify-between border-b border-[#E8EAEE] pb-3">
              <div className="flex w-full items-center justify-between">
                <div className="flex h-[4.1875rem] w-28 flex-col items-start">
                  <div className="flex h-[1.875rem] items-center">
                    <img src={incomeIcon} alt="" className="h-6 w-6" />
                    <div className="ml-2 w-20 text-xs font-medium leading-none text-[var(--font-color-second)]">
                      Cumulative income
                    </div>
                  </div>
                  <div className="mt-2 font-[DIN] text-2xl font-bold text-[var(--basic-color-primary)]">
                    {money(allAmount)}
                  </div>
                </div>
                <div className="h-16 w-px bg-[#E8EAEE]" />
                <div className="flex h-[4.1875rem] w-28 flex-col items-end">
                  <div className="relative left-[0.35rem] flex h-[1.875rem] items-center">
                    <img src={countIcon} alt="" className="h-6 w-6" />
                    <div className="ml-2 w-20 text-xs font-medium leading-none text-[var(--font-color-second)]">
                      Total Invited Count
                    </div>
                  </div>
                  <div className="mt-2 font-[DIN] text-2xl font-bold text-[var(--basic-color-primary)]">
                    {invitedNumber}
                  </div>
                </div>
              </div>
              {userList[2]?.inviteNumber === userList[2]?.completedNumber &&
                userInfo?.isAgent !== 1 && (
                  <button
                    type="button"
                    onClick={toAgentApply}
                    className="mt-3 h-12 w-full rounded-[2.5rem] border-0 bg-[var(--basic-color-primary)] text-sm font-bold text-white">
                    <div>Join Agency</div>
                    <div className="text-[10px] font-medium">
                      Be an agent and earn more commission.
                    </div>
                  </button>
                )}
            </div>
            <div className="flex h-7 items-center justify-between">
              <InvitationAction
                title="Invitation record"
                type="record"
                onClick={() => goTo('InvitationRecord')}
              />
              <InvitationAction
                title="Invitation reward rules"
                type="rules"
                onClick={() => goTo('InvitationRule')}
              />
            </div>
          </div>

          <div className="flex flex-col gap-y-3">
            {userList.map(item => (
              <BonusCard key={item.level} item={item} onShare={handleShare} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referral;
